import { Storage } from './storage';
import { fetchBanepadDevices } from './api/banepadDevices';
import { isBanepadDevicePaired, padApiModeActive, postPadHeartbeat } from './api/pad';

// ─────────────────────────────────────────────────────────────────────
// BANEPAD HEARTBEAT — «is this pad alive?»
//
// A pad reports in every BANEPAD_HEARTBEAT_MS; Sentralen derives presence from
// the last beat, and a pad silent for longer than BANEPAD_OFFLINE_AFTER_MS is
// «frakoblet».
//
// TWO PATHS, one shape.
//
//   API path      the pad POSTs /pad/heartbeat with its device token and the
//                 server stamps `last_seen_at` on the device ROW. The pad
//                 names no device and no court — both come from the token — so
//                 it can only report itself. Sentralen reads presence from the
//                 registry it already fetches.
//
//   legacy path   each pad writes `banepad_heartbeat_v1:<deviceId>` through
//                 Storage and Sentralen lists those keys. Kept verbatim
//                 through the cutover; it now runs against `/kv` on the API
//                 rather than the retired Supabase store.
//
// `PadHeartbeat` stays the currency of both, so `padPresence`, `padStatusChip`
// and the central view are untouched — the API path REBUILDS this shape from
// the device rows rather than introducing a second one. See
// docs/banepad/device-security.md.
// ─────────────────────────────────────────────────────────────────────

export const BANEPAD_HEARTBEAT_KEY_PREFIX = 'banepad_heartbeat_v1:';
/** How often a paired pad reports in. */
export const BANEPAD_HEARTBEAT_MS = 30_000;
/** Sentralen: silence longer than this = the pad is reported as offline. */
export const BANEPAD_OFFLINE_AFTER_MS = 3 * 60_000;
/** The pad itself: failed heartbeats for this long = show the support screen. */
export const BANEPAD_PAD_OFFLINE_AFTER_MS = 2 * 60_000;

export interface PadHeartbeat {
  deviceId: string;
  courtId: number;
  courtName: string;
  /** Epoch ms of the beat (the pad's clock). */
  at: number;
  /** 'app' = the native kiosk shell (apps/mobile), 'browser' = a plain browser tab. */
  shell: 'app' | 'browser';
  appVersion?: string;
}

export type PadPresence = 'online' | 'offline' | 'unknown';

/**
 * Pure: presence of a pad from its last heartbeat. `unknown` = never heard
 * from (e.g. an old pad that predates heartbeats).
 */
export function padPresence(
  hb: PadHeartbeat | null | undefined,
  now: number,
  offlineAfterMs: number = BANEPAD_OFFLINE_AFTER_MS,
): { presence: PadPresence; silentMs: number } {
  if (!hb || !(hb.at > 0)) return { presence: 'unknown', silentMs: 0 };
  const silentMs = Math.max(0, now - hb.at);
  return { presence: silentMs > offlineAfterMs ? 'offline' : 'online', silentMs };
}

/** Pure: «2 min», «1 t 5 min», «3 d 2 t» — how long a pad has been silent. */
export function formatSilence(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} t ${min % 60} min`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} t`;
}

/** Pure: the storage key for a device's heartbeat. */
export function heartbeatKey(deviceId: string): string {
  return BANEPAD_HEARTBEAT_KEY_PREFIX + deviceId;
}

/**
 * Report one pad alive. Throws when the backend is unreachable — the caller
 * counts a throw as a missed beat, which is how the pad notices it is offline.
 *
 * On the API path the argument is mostly IGNORED, deliberately: the device id,
 * the court and the timestamp are the server's to decide, and only the two
 * diagnostic strings are the pad's to report. The full record is still taken
 * here so the legacy path and every caller keep one signature.
 */
export async function writePadHeartbeat(hb: PadHeartbeat): Promise<void> {
  if (padApiModeActive()) {
    // NOT PAIRED = NOT A PAD ON THIS PATH. Beating into /kv instead would be the
    // device write this whole change removes. The question is now «is this tablet
    // paired» rather than «does it hold a token» (#663) — the credential is an
    // httpOnly cookie the transport carries.
    if (!isBanepadDevicePaired()) throw new Error('[pad] heartbeat: this pad is not paired');
    await postPadHeartbeat({ appVersion: hb.appVersion, shell: hb.shell });
    return;
  }
  await Storage.set(heartbeatKey(hb.deviceId), JSON.stringify(hb));
}

/**
 * Rebuild the heartbeat map from the device registry (the API path).
 *
 * KEYED BY THE REGISTRY ID, which is what Sentralen looks up: its rows come
 * from the same GET, and `presenceForCourt` does `heartbeats[reg.id]`. The pad
 * and the registry were briefly in two different id spaces — the pad was told
 * the row's uuid — and because a missing heartbeat reads as `unknown` rather
 * than `offline`, every pad reported «Pad koblet» whether it was alive or not.
 * /pad/me now answers with this id.
 *
 * A device with no `lastSeenAt` is simply absent, which is exactly how a pad
 * that has never beaten was represented before.
 */
function heartbeatsFromDevices(devices: Record<string, unknown>[]): Record<string, PadHeartbeat> {
  const out: Record<string, PadHeartbeat> = {};
  for (const d of devices) {
    const id = String(d?.id ?? '');
    const at = Number(d?.lastSeenAt);
    if (!id || !Number.isFinite(at) || at <= 0) continue;
    out[id] = {
      deviceId: id,
      courtId: Number(d?.courtId) || 0,
      courtName: String(d?.courtName ?? ''),
      at,
      // Diagnostics the pad reported, merged out of `extra` by the API.
      shell: d?.shell === 'app' ? 'app' : 'browser',
      appVersion: typeof d?.appVersion === 'string' ? d.appVersion : undefined,
    };
  }
  return out;
}

/** All heartbeats, keyed by device id (missing/garbled records are skipped). */
export async function readPadHeartbeats(): Promise<Record<string, PadHeartbeat>> {
  if (padApiModeActive()) {
    return heartbeatsFromDevices((await fetchBanepadDevices()) as Record<string, unknown>[]);
  }
  const out: Record<string, PadHeartbeat> = {};
  const { keys } = await Storage.list(BANEPAD_HEARTBEAT_KEY_PREFIX);
  await Promise.all(
    (keys || []).map(async (key) => {
      try {
        const res = await Storage.get(key);
        if (!res?.value) return;
        const hb = JSON.parse(res.value) as PadHeartbeat;
        if (hb && typeof hb === 'object' && hb.deviceId) out[hb.deviceId] = hb;
      } catch {
        /* skip a garbled record */
      }
    }),
  );
  return out;
}

/**
 * Pure: the court card's pad chip in Sentralen — label, state and tooltip.
 *
 * The label stays SHORT on purpose. The card is ~240px wide in the grid and
 * the chip shares a row with «Åpne pad»; a label that grows with the outage
 * («Pad frakoblet · 6 t 26 min») overflowed and painted over that control.
 * The duration lives in the tooltip and in the banner above the grid, which
 * lists every silent pad, so nothing is lost from the page.
 */
export function padStatusChip(
  paired: boolean,
  presence: { presence: PadPresence; silentMs: number; heartbeat: PadHeartbeat | null } | null,
): { label: string; offline: boolean; title?: string } {
  if (!paired) return { label: 'Koble pad', offline: false };

  const lastSeen = presence?.heartbeat
    ? `sist sett ${new Date(presence.heartbeat.at).toLocaleTimeString('nb-NO', {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  if (presence?.presence === 'offline') {
    const parts = [`Frakoblet i ${formatSilence(presence.silentMs)}`];
    if (lastSeen) parts.push(lastSeen);
    return { label: 'Pad frakoblet', offline: true, title: parts.join(' · ') };
  }

  return { label: 'Pad koblet', offline: false, title: lastSeen ?? undefined };
}
